import { useContext, useEffect, useState } from 'react';
import {
    JsonHubProtocol,
    HubConnectionState,
    HubConnectionBuilder,
    LogLevel,
    HubConnection,
    IHttpConnectionOptions,
    HttpRequest,
    HttpClient,
} from '@microsoft/signalr';
import { useAuthStore } from '../context/AuthContext';
import { useConnectionStore } from '../context/ConnectionContext';
import { HttpResponse } from '@microsoft/signalr/dist/esm/HttpClient';
import { useAolUpdateHandler } from './useAolUpdateHandler';
import { AolAddedDTO, AolSectionSignalRDTO, AolSectionSignalRUpdate } from '../types/Dto/AolSectionSignalRDTO';
import { AolSectionDataUpdateDto } from '../types/Dto/AolSectionDataUpdateDto';
import { TankersEventDataBM } from '../types/BusinessModel/TankersEventDataBM';
import useEvent from './useEvent';
import { ValidatedNoonReportFactory } from '../factory/ValidatedNoonReportFactory';
import { ValidatedNoonReportBM } from '../types/BusinessModel/ValidatedNoonReportBM';
import { ImosPortCallSyncDto } from '../types/Dto/ImosPortCallSyncDto';
import { useToast } from './useToast';
import { useVesselStore } from '../context/VesselContext';
import { SignalRResponse } from '../types/Dto/SignalRResponse';
import { SignalRMessageType } from '../types/Dto/SignalRMessageType';
import { useSWService } from '../api/sw/useSWService';
import useApiEndpoints from '../context/useApiEndpoints';
import { buildQueryParams } from '../api/endpoints/queryParamsHelper';
import useApi from '../api/useApi';
import { useAolStore } from '../context/AolContext';
import { makeAolDataItemList, makeAolGroupList } from '../factory/Aol/AolDataItemFactory';
import useGetAolData from '../api/aol/useGetAolData';
import { GetSectionBM } from '../utils/aolSectionHelper';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { getStringValue } from '../utils/arrayUtils';
import useCargoTemperatureApi from '../api/cargo-temp-log/useCargoTempLogApi';
import getContext from '../context/AppSettingsContext';
import { formatAolUrlDate } from '../utils/aol/aolDateUtils';
import useUpdateMissingTasksIcon from './useUpdateMissingTasksIcon';
import { AppFeatureEnum } from '../enums/appFeatureEnum';
import useAppFeature from './useAppFeature';
import { useCargoTempLogStore } from '../context/CargoTempLogContext';
import { CargoTemperatureLogDate } from '../types/ViewModel/CargoTempLog/CargoTemperatureLogDate';
import { BunkeringBM } from 'src/types/BusinessModel/Bunkering/BunkeringBM';
import { useBunkerUpdateHandler } from './useBunkerUpdateHandler';
import { BunkeringStatusBM } from 'src/types/BusinessModel/Bunkering/BunkeringStatusBM';
import { FuelRobBM } from 'src/types/BusinessModel/Bunkering/FuelRobBM';
import { getAllReportEventsItems } from 'src/api/report-events/offlineReportEventsApi';
import { getAllFuelRobItems } from 'src/api/bunkering/offlineBunkeringApi';
import { useRotationStore } from 'src/context/RotationContext';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { useStowagePlanResponseHandler } from 'src/api/responseHandlers/useStowagePlanResponseHandler';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';
import useGetMasterPlanApi from 'src/api/stowage/useGetMasterPlanApi';
import { requestThrottleManager } from 'src/utils/requestThrottleManager';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';

export default () => {
    const { setSignalRConnectionId, loggedInUser } = useAuthStore();
    const { selectedVessel } = useVesselStore();
    const { setConnectionStatus, hubConnection, setHubConnection, connectedVesselGroup, setConnectedVesselGroup, isOfflineSimulated } =
        useConnectionStore();
    const [signalRResponse, setSignalRResponse] = useState<SignalRResponse>();
    const { updateAolList, addNoonReport, updateOnDelete, updateAolStatusToOtherUsers, toggleToast, updateOnHoursSync, updateOnImosPortCallIdSync } =
        useAolUpdateHandler();
    const { handleUpdatedBunkering, handleUpdatedBunkeringStatus, handleDeletedBunkeringStatus, handleUpdatedRobs, toggleBunkerToast } =
        useBunkerUpdateHandler();
    const { toastSuccess, toastError } = useToast();
    const { updateDeltaEventArray } = useEvent();
    const { refreshRequestsContaining, removeRequestsContaining } = useSWService();
    const { apiEndpoints } = useApiEndpoints();
    const { getNoonReportSection, getAolItems } = useGetAolData();
    const { getMasterStowagePlanFromApi } = useGetMasterPlanApi();
    const { setAolItems, aolFilter, setHasSyncError } = useAolStore();
    const { cargoTempLogDates } = useCargoTempLogStore();
    const validatedNoonReportFactory = new ValidatedNoonReportFactory();
    const { getApiData, postApiData } = useApi();
    const location = useLocation();
    const { getCargoTemperatureConfigs, getCargoTemperatureInputFields } = useCargoTemperatureApi();
    const appSettings = useContext(getContext());
    const { updateAolMissingTasks } = useUpdateMissingTasksIcon();
    const { isFeatureEnabled } = useAppFeature();
    const { productList, portNameList, berthNameList, stowagePlanRotationPortCallList, stowagePlanRotationCargoList } = useRotationStore();
    const { activeStowagePlan } = useStowagePlanStore();

    const MAX_SIGNALR_RETRY_ATTEMPTS = 15;
    const { onStowagePlanUpdated, onStowagePlanAdded, updateLoadedMasterStowagePlan } = useStowagePlanResponseHandler();

    const getUpdatedSection = async (sectionSignalRUpdate: AolSectionSignalRUpdate) => {
        if (!selectedVessel?.imoNumber || !loggedInUser) {
            return;
        }
        const updatedNoonReport = await getNoonReportSection(
            selectedVessel.imoNumber,
            0,
            sectionSignalRUpdate.section,
            true,
            sectionSignalRUpdate.noonReportGuid
        );
        const updatedSection = GetSectionBM(sectionSignalRUpdate.section, updatedNoonReport);

        const dto: AolSectionSignalRDTO = {
            section: updatedSection,
            noonReportId: updatedNoonReport?.metadata?.noonReportId,
            noonReportGuid: sectionSignalRUpdate.noonReportGuid,
            imoNumber: sectionSignalRUpdate.imoNumber,
            indexInDay: 0,
        };
        updateAolList(dto);
        toggleToast(dto.noonReportId ?? 0, signalRResponse, hubConnection);
    };

    const getDistinctCargoNumbers = (logDates: CargoTemperatureLogDate[]): string[] => {
        const cargoNumbersSet = new Set<string>();

        logDates.forEach((logDate) => {
            logDate.cargoInTankList?.forEach((cargoInTank) => {
                cargoNumbersSet.add(cargoInTank.cargoNumber);
            });
        });

        return Array.from(cargoNumbersSet);
    };

    const eventMessageHandler = async () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as TankersEventDataBM;
        if (dto.events && dto.events.length > 0) {
            const offlineReportEvents = await getAllReportEventsItems();

            refreshRequestsContaining(
                [apiEndpoints['getTankersEventDataDeltaForVoyage'].url],
                'voyageNumber1=' + dto.events[0].voyageNumber + '&imoNumber=' + dto.imoNumber
            );
            refreshRequestsContaining([apiEndpoints['getTankersEventDataDeltaForVoyageList'].url]);
            updateDeltaEventArray(dto, offlineReportEvents);
            toastSuccess(signalRResponse.sender + ' has updated events');
        }
    };

    const aolSectionMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as AolSectionSignalRDTO;

        // Use throttled refresh instead of direct call
        throttledRefreshNoonReports(dto.imoNumber);

        updateAolList(dto);
        toggleToast(+dto.noonReportId, signalRResponse, hubConnection);
    };

    const syncNoonReportFuelRobMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as AolSectionSignalRDTO;

        // Use throttled refresh instead of direct call
        throttledRefreshNoonReports(dto.imoNumber);

        if (location.pathname === '/aol') {
            setHasSyncError(false);

            const fromDate = formatAolUrlDate(aolFilter.fromDate);
            const toDate = formatAolUrlDate(aolFilter.toDate);
            const aolItemsPromise = getAolItems(dto.imoNumber, fromDate, toDate, true);
            const noonReportsPromise = getNoonReports(dto.imoNumber, fromDate, toDate);
            const loadedMasterStowagePlanPromise = getMasterStowagePlanFromApi(selectedVessel);

            Promise.all([aolItemsPromise, noonReportsPromise, loadedMasterStowagePlanPromise]).then(async (values) => {
                setAolItems([...values[0]]);
                const _aolDataItems = await makeAolDataItemList(values[0], values[1], isFeatureEnabled, selectedVessel, values[2]?.data);
                const _aolGroups = makeAolGroupList(_aolDataItems, selectedVessel);
                updateAolList(dto, { aolDataItems: _aolDataItems, aolGroups: _aolGroups });
                console.log('updateAolList has been called');
                toggleToast(+dto.noonReportId, signalRResponse, hubConnection);
            });
        }
    };

    const aolSectionUpdateMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const sectionSignalRUpdate = text as AolSectionSignalRUpdate;
        getUpdatedSection(sectionSignalRUpdate);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(sectionSignalRUpdate.imoNumber);
    };

    const aolSectionDataUpdateMessageToOtherUsersHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as AolSectionDataUpdateDto;
        updateAolStatusToOtherUsers(dto, signalRResponse.sender);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(dto.imoNumber);
    };

    const addAolMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as AolAddedDTO;
        addNoonReport(validatedNoonReportFactory.create(dto.noonReport as ValidatedNoonReportBM));

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(dto.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(dto.imoNumber);
        }
    };

    const deleteAolMessageHandler = () => {
        const deletePayload = JSON.parse(signalRResponse.responseObject);
        updateOnDelete(undefined, deletePayload.noonReportGuid);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(+signalRResponse.imoNumber);

        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Notifications, date: new Date() })) {
            updateAolMissingTasks(+signalRResponse.imoNumber);
        }
    };

    const syncNoonReportPortLogHoursMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const noonReportBM = text as ValidatedNoonReportBM;
        const noonReport = validatedNoonReportFactory.create(noonReportBM);
        updateOnHoursSync(noonReport);
        toggleToast(noonReport.metadata.noonReportId, signalRResponse, hubConnection);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(noonReport.metadata.imoNumber);
    };

    const syncNoonReportImosPortCallIdMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const dto = text as ImosPortCallSyncDto;
        updateOnImosPortCallIdSync(0, +dto.imosPortCallId, dto.noonReportGuid);
        toggleToast(0, signalRResponse, hubConnection);

        // Use throttled refresh instead of direct calls
        throttledRefreshNoonReports(+signalRResponse.imoNumber);
    };

    const saveCargoTemperatureConfigsMessageHandler = () => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
                getCargoTemperatureInputFields(selectedVessel?.imoNumber, true);
                toastSuccess(signalRResponse?.sender + ' has updated cargo temp. configuration');
            }
        }
    };

    const saveCargoTemperatureLogsMessageHandler = () => {
        // Refresh cargo temp log dates for other users when someone saves a log
        refreshRequestsContaining([apiEndpoints['getCargoTempLogDates'].url]);
    };

    const deleteCargoTemperatureConfigsMessageHandler = () => {
        if (cargoTempLogDates != null && cargoTempLogDates.length > 0) {
            const cargoNumbers = getDistinctCargoNumbers(cargoTempLogDates);
            if (cargoNumbers && cargoNumbers.length > 0) {
                const cargoNumbersStr = getStringValue(cargoNumbers, ',');
                getCargoTemperatureConfigs(selectedVessel?.imoNumber, cargoNumbersStr, true);
            }
        }
    };

    const upsertBunkeringMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const bunkeringBM = text as BunkeringBM;
        handleUpdatedBunkering(bunkeringBM);
        toggleBunkerToast(signalRResponse, hubConnection, 'bunkering data');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertBunkeringStatusMessageHandler = () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const bunkeringStatusBM = text as BunkeringStatusBM;
        handleUpdatedBunkeringStatus(bunkeringStatusBM);
        toggleBunkerToast(signalRResponse, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const deleteBunkeringStatusMessageHandler = () => {
        const bunkeringStatusGuid = signalRResponse.responseObject as string;
        handleDeletedBunkeringStatus(bunkeringStatusGuid);
        toggleBunkerToast(signalRResponse, hubConnection, 'a bunkering status');
        refreshRequestsContaining([apiEndpoints['getBunkerings'].url]);
    };

    const upsertRobsMessageHandler = async () => {
        const text = JSON.parse(signalRResponse.responseObject);
        const fuelRobData = text as FuelRobBM[];
        const offlineFuelRobs = await getAllFuelRobItems();

        handleUpdatedRobs(fuelRobData, offlineFuelRobs);
        toggleBunkerToast(signalRResponse, hubConnection, 'ROB data');
        refreshRequestsContaining([apiEndpoints['getFuelRobData'].url]);
    };

    const upsertBunkerLossMessageHandler = () => {
        toggleBunkerToast(signalRResponse, hubConnection, 'bunker loss data');
        refreshRequestsContaining([apiEndpoints['getBunkerLossData'].url]);
    };

    const imosUpdatesHandler = () => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const updatedVoyages = signalRResponse.responseObject as number[];

            console.log('IMOS data has been updated...', updatedVoyages);
            toastSuccess('IMOS data has been updated...');

            if (!activeStowagePlan) {
                return;
            }

            refreshRequestsContaining([apiEndpoints['getRotation'].url], null, {
                productList,
                portNameList,
                berthNameList,
                selectedVessel,
                stowagePlanGuid: activeStowagePlan.stowagePlanGuid,
                stowagePlanTransitOperations: activeStowagePlan.transitOperations,
                stowagePlanRotationPortCallList,
                stowagePlanRotationCargoList,
            });
        }
    };
    const stowagePlanUpdatedHandler = () => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(signalRResponse.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            if (
                updatedStowagePlanBM.type === StowagePlanTypeEnum.Master &&
                isFeatureEnabled({ featureEnum: AppFeatureEnum.StowageIntegration, imoNumber: selectedVessel.imoNumber, date: new Date() })
            ) {
                // Handle master stowage plan change
                updateLoadedMasterStowagePlan(updatedStowagePlanBM, signalRResponse?.sender);
                refreshRequestsContaining([apiEndpoints['getMasterStowagePlan'].url]);
            }

            if (location.pathname === '/stowage') {
                onStowagePlanUpdated(updatedStowagePlanBM, signalRResponse?.sender);
                refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                refreshRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            } else {
                removeRequestsContaining([apiEndpoints['getStowagePlans'].url]);
                removeRequestsContaining(
                    [apiEndpoints['getStowagePlanChanges'].url],
                    buildQueryParams({ stowagePlanGuid: updatedStowagePlanBM.stowagePlanGuid.toString() })
                );
            }
        }
    };

    const stowagePlanAddedHandler = () => {
        if (isFeatureEnabled({ featureEnum: AppFeatureEnum.Stowage, date: new Date(), imoNumber: selectedVessel?.imoNumber })) {
            const text = JSON.parse(signalRResponse.responseObject);
            const updatedStowagePlanBM = text as StowagePlanBM;

            onStowagePlanAdded(updatedStowagePlanBM, signalRResponse?.sender);
            refreshRequestsContaining([apiEndpoints['getStowagePlans'].url]);
        }
    };

    const handleSignalRResponse = async (responseType: SignalRMessageType, signalRResponse: SignalRResponse, hubConnection: HubConnection) => {
        const responseHandlers: { [key in SignalRMessageType]?: { otherUsersHandler: () => void; senderHandler: () => void | null } } = {
            event: { otherUsersHandler: eventMessageHandler, senderHandler: null },
            aolSection: { otherUsersHandler: aolSectionMessageHandler, senderHandler: null },
            syncNoonReportFuelRob: { otherUsersHandler: syncNoonReportFuelRobMessageHandler, senderHandler: null },
            aolSectionUpdate: { otherUsersHandler: aolSectionUpdateMessageHandler, senderHandler: null },
            aolSectionDataUpdate: {
                otherUsersHandler: aolSectionDataUpdateMessageToOtherUsersHandler,
                senderHandler: null,
            },
            addAol: { otherUsersHandler: addAolMessageHandler, senderHandler: null },
            deleteAol: { otherUsersHandler: deleteAolMessageHandler, senderHandler: null },
            syncNoonReportPortLogHours: { otherUsersHandler: syncNoonReportPortLogHoursMessageHandler, senderHandler: null },
            syncNoonReportImosPortCallId: { otherUsersHandler: syncNoonReportImosPortCallIdMessageHandler, senderHandler: null },
            saveCargoTemperatureConfigs: { otherUsersHandler: saveCargoTemperatureConfigsMessageHandler, senderHandler: null },
            upsertBunkering: { otherUsersHandler: upsertBunkeringMessageHandler, senderHandler: null },
            upsertBunkeringStatus: { otherUsersHandler: upsertBunkeringStatusMessageHandler, senderHandler: null },
            deleteBunkeringStatus: { otherUsersHandler: deleteBunkeringStatusMessageHandler, senderHandler: null },
            upsertRobs: { otherUsersHandler: upsertRobsMessageHandler, senderHandler: null },
            upsertBunkerLoss: { otherUsersHandler: upsertBunkerLossMessageHandler, senderHandler: null },
            imosUpdates: { otherUsersHandler: imosUpdatesHandler, senderHandler: null },
            stowagePlanUpdated: { otherUsersHandler: stowagePlanUpdatedHandler, senderHandler: null },
            stowagePlanAdded: { otherUsersHandler: stowagePlanAddedHandler, senderHandler: null },
            deleteCargoTemperatureConfigs: { otherUsersHandler: deleteCargoTemperatureConfigsMessageHandler, senderHandler: null },
            saveCargoTemperatureLogs: { otherUsersHandler: saveCargoTemperatureLogsMessageHandler, senderHandler: null },
        };

        const handlers = responseHandlers[responseType];
        if (handlers) {
            if (signalRResponse.connectionId !== hubConnection.connectionId) {
                await handlers.otherUsersHandler?.();
            } else {
                await handlers.senderHandler?.();
            }
        }
    };

    useEffect(() => {
        (async () => {
            if (signalRResponse && hubConnection) {
                if (signalRResponse.imoNumber === selectedVessel?.imoNumber?.toString()) {
                    await handleSignalRResponse(signalRResponse.type, signalRResponse, hubConnection);
                } else {
                    console.log(
                        'Rejecting SignalR message: signalRResponse.imoNumber = ' +
                            signalRResponse.imoNumber +
                            ' and selectedVessel?.imoNumber = ' +
                            selectedVessel?.imoNumber
                    );
                }
            }
        })();
    }, [signalRResponse]);

    const getNoonReports = async (imoNumber: number, fromDate: string, toDate: string) => {
        // Create a unique key for this request
        const requestKey = `SignalR:getNoonReportsForShip:${imoNumber}:${fromDate}:${toDate}`;

        // Use throttle manager for deduplication and caching
        return requestThrottleManager.execute(requestKey, async () => {
            const result = await getApiData({
                endpoint: apiEndpoints['getNoonReportsForShip'].url,
                queryParam: buildQueryParams({ imoNumber, fromDate, toDate }),
                forceRefreshCache: true,
            });

            if (result) {
                return validatedNoonReportFactory.createList(result);
            }
            return [];
        });
    };

    /**
     * Optimized handler for refreshing GetNoonReportsForShip endpoint
     * Uses deduplication to prevent multiple parallel calls
     */
    const throttledRefreshNoonReports = (imoNumber?: number) => {
        if (!imoNumber && selectedVessel) {
            imoNumber = selectedVessel.imoNumber;
        }

        if (!imoNumber) return;

        // Create a request-specific key based on the current vessel
        const requestKey = `refreshNoonReports:${imoNumber}`;

        // Use our throttle manager to deduplicate requests
        if (!requestThrottleManager.isRequestInProgress(requestKey)) {
            requestThrottleManager.execute(requestKey, async () => {
                refreshRequestsContaining([apiEndpoints['getNoonReportsForShip'].url]);
                return true;
            });
        } else {
            console.log('Skipping duplicate refresh of GetNoonReportsForShip endpoint');
        }
    };

    const setupSignalRConnection = (url: string, imoNumber: number) => {
        const customHttpClient: HttpClient = new (class extends HttpClient {
            async send(request: HttpRequest): Promise<HttpResponse> {
                try {
                    let response;
                    if (request.method === 'POST') {
                        response = await postApiData({
                            endpoint: request.url,
                            disableAppSettingsApiEndpoint: true,
                            returnFullResponseObject: true,
                            data: request.content,
                        });
                    } else if (request.method === 'GET') {
                        response = await axios.get(request.url);
                    } else if (request.method === 'DELETE') {
                        response = await axios.delete(request.url);
                    } else {
                        response = await axios({ method: request.method?.toLowerCase(), url: request.url, data: request.content });
                    }

                    if (!response) {
                        return new HttpResponse(500, 'Internal Error', 'Request failed');
                    }

                    const responseBody = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
                    if (response.status === 200 && response.data?.accessToken) {
                        localStorage.setItem('signalRConnection', responseBody);
                    }
                    return new HttpResponse(response.status, response.statusText, responseBody);
                } catch (error) {
                    return new HttpResponse(500, 'Internal Error', error?.message || 'Request failed');
                }
            }
        })();

        const options: IHttpConnectionOptions = {
            accessTokenFactory: () => {
                return '';
            },
            httpClient: customHttpClient,
        };

        const connection = new HubConnectionBuilder()
            .withUrl(url, options)
            .withAutomaticReconnect()
            .withHubProtocol(new JsonHubProtocol())
            .configureLogging(LogLevel.Information)
            .build();

        console.log('Connection: ' + connection.connectionId);
        // Note: to keep the connection open the serverTimeout should be
        // larger than the KeepAlive value that is set on the server
        // keepAliveIntervalInMilliseconds default is 15000 and we are using default
        // serverTimeoutInMilliseconds default is 30000 and we are using 60000 set below
        connection.serverTimeoutInMilliseconds = 60000;

        // re-establish the connection if connection dropped
        connection.onclose((error) => {
            console.assert(connection.state === HubConnectionState.Disconnected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Disconnected);
            }
            console.log('Connection closed due to error. Try refreshing this page to restart the connection.', error);
        });

        connection.onreconnecting((error) => {
            console.assert(connection.state === HubConnectionState.Reconnecting);
            console.log('Connection lost due to error. Reconnecting...', error);
        });

        connection.onreconnected((connectionId) => {
            console.assert(connection.state === HubConnectionState.Connected);
            if (setConnectionStatus) {
                setConnectionStatus(HubConnectionState.Connected);
            }
            console.log('Connection re-established. Connected with connectionId: ', connectionId);
        });

        startSignalRConnection(connection, url, imoNumber);
        return connection;
    };

    const startSignalRConnection = async (connection: HubConnection, url: string, imoNumber: number, attempts = 0) => {
        try {
            await throwErrorIfOfflineIsSimulated();
            if (connection) {
                await connection.start();

                if (connection.connectionId && connection.state === HubConnectionState.Connected) {
                    if (setConnectionStatus) {
                        setConnectionStatus(HubConnectionState.Connected);
                    }
                    setSignalRConnectionId(connection.connectionId);
                    setHubConnection(connection);
                }
            }
        } catch (error) {
            if (await isOfflineSimulated()) return;
            console.error('SignalR Connection Error: ', error);

            if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                console.warn('SignalR: Max connection attempts reached, entering dormant mode');
                toastError('Connection lost — will auto-reconnect when online');
                return;
            }

            const delay = attempts < 5 ? 5000 : 30000;
            setTimeout(() => startSignalRConnection(connection, url, imoNumber, attempts + 1), delay);
        }
    };

    const tryConnectToGroup = async (connection: HubConnection, imoNumber: number, attempts = 0) => {
        if (!imoNumber || !connection || connection.state !== HubConnectionState.Connected) return;
        if (connectedVesselGroup !== imoNumber) {
            connectToGroup(connection, appSettings.api, imoNumber)
                .then(() => {
                    hubConnection.on('newMessage', (res) => {
                        setSignalRResponse(res);
                    });
                    setConnectedVesselGroup(imoNumber);
                })
                .catch(async () => {
                    if (await isOfflineSimulated()) return;
                    console.log('Failed to join SignalR group, retrying');

                    if (attempts >= MAX_SIGNALR_RETRY_ATTEMPTS) {
                        console.warn('SignalR: Max group join attempts reached, entering dormant mode');
                        toastError('Connection lost — will auto-reconnect when online');
                        return;
                    }

                    const delay = attempts < 5 ? 5000 : 30000;
                    setTimeout(() => tryConnectToGroup(connection, imoNumber, attempts + 1), delay);
                });
        }
    };

    useEffect(() => {
        if (selectedVessel && hubConnection && hubConnection.state === HubConnectionState.Connected) {
            if (connectedVesselGroup && connectedVesselGroup !== selectedVessel.imoNumber) {
                leaveGroup(hubConnection, appSettings.api, connectedVesselGroup).finally(() =>
                    tryConnectToGroup(hubConnection, selectedVessel.imoNumber)
                );
            } else {
                tryConnectToGroup(hubConnection, selectedVessel.imoNumber);
            }
        }
    }, [hubConnection, selectedVessel]);

    const leaveGroup = async (connection: HubConnection, url: string, imoNumber: number) => {
        return await getApiData({
            endpoint: url + 'LeaveGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
    };

    const stopSignalRConnection = () => {
        if (hubConnection) {
            hubConnection.stop();
            setConnectionStatus(HubConnectionState.Disconnected);
            localStorage.setItem('signalRConnection', '');
        }
    };

    const connectToGroup = async (connection: HubConnection, url: string, imoNumber: number) => {
        return await getApiData({
            endpoint: url + 'JoinGroup',
            disableAppSettingsApiEndpoint: true,
            queryParam: buildQueryParams({ connectionId: connection.connectionId, imoNumber: imoNumber }),
        });
    };

    const throwErrorIfOfflineIsSimulated = async () => {
        const _isOfflineSimulated = await isOfflineSimulated();
        if (_isOfflineSimulated) throw new Error('offline');
    };

    return { setupSignalRConnection, stopSignalRConnection };
};
